<template>
  <AIBasePanel
    auto-scroll
    @close="reset('close')"
    @home="reset('home')"
  >
    <template #toolbarCenter>
      <div class="tw:font-bold">
        {{ getLocaleText("ai_mention_streams_assistant_panel.title") }}
      </div>
    </template>

    <AIAssistantBaseChat
      :messages="messages"
      @suggestion="handleSuggestion"
    >
      <template #modelControls="{ modelMessage }">
        <AIMentionStreamsChatCard :message="modelMessage" />
      </template>
    </AIAssistantBaseChat>

    <template #footer>
      <AIAssistantBaseChatInput
        ref="inputRef"
        v-model="input"
        :loading="loading"
        :placeholder="getLocaleText('ai_assistant_panel.ask_me_anything')"
        @submit="handleSubmit"
      />
    </template>
  </AIBasePanel>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref, useTemplateRef, watch } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import AIAssistantBaseChat from "shared/components/AI/AIAssistant/AIAssistantBaseChat.vue";
import AIAssistantBaseChatInput from "shared/components/AI/AIAssistant/AIAssistantBaseChatInput.vue";
import AIBasePanel from "shared/components/AI/AIBasePanel.vue";
import AIMentionStreamsChatCard from "shared/components/AI/AIMentionStreams/AIMentionStreamsChatCard.vue";
import { useAIMentionStreamAgent } from "shared/composables/useAIMentionStreamAgent";

const emit = defineEmits(["close", "home"]);

const input = ref<string>("");
const inputRef = useTemplateRef("inputRef");

const chatSessionId = ref<string>("");

const { messages, loading, add, submit } = useAIMentionStreamAgent(
  {
    source: "create mention streams",
  },
  chatSessionId
);

function reset(event: "close" | "home") {
  emit(event);
}

function handleSuggestion(suggestion: string) {
  if (loading.value) return;
  submit(suggestion);
}

function handleSubmit() {
  submit(input.value);
  input.value = "";
}

function addInitialMessages() {
  add([
    {
      role: "user",
      message: getLocaleText("ai_mention_streams_assistant_panel.title"),
    },
    {
      role: "suggest",
      message: getLocaleText(
        "ai_mention_streams_assistant_panel.option_prompt"
      ),
      options: [
        {
          label: getLocaleText(
            "ai_mention_streams_assistant_panel.create_mention_stream_option"
          ),
          tooltipText: getLocaleText(
            "ai_mention_streams_assistant_panel.create_mention_stream_option"
          ),
        },
        {
          label: getLocaleText(
            "ai_mention_streams_assistant_panel.create_multiple_mention_streams_option"
          ),
          tooltipText: getLocaleText(
            "ai_mention_streams_assistant_panel.create_multiple_mention_streams_option"
          ),
        },
      ],
    },
  ]);
}

watch(loading, async (value) => {
  if (value) return;
  await nextTick();
  inputRef.value?.focus();
});

onMounted(() => {
  addInitialMessages();
});
</script>
